// Find · FILTERS · sort · page over rows the CALLER already holds — for an
// AeronetDataGridPremium detail tab whose route is not paginated (BP-80's
// "route that is not paginated" case) or whose rows arrive whole on the record.
//
// The Premium grid is server-mode (filterMode / sortingMode / paginationMode
// "server"): it never filters, sorts or pages `rows` itself, it hands the
// request to `data(params)` —
//   { page (1-based), size, q?, filter?: JSON strings, sort?: "-a,b" }
// (DataGridPremium.tsx getData). A caller that owns the whole row set has to
// apply those params here, or Find and FILTERS are dead controls: they update
// the model, `data` ignores it, and the grid shows the same rows.
//
// `valueOf(row, field)` returns what the COLUMN SHOWS for that field — the same
// value it sorts on — so a Find for "C-GKOF" matches the registration the cell
// displays, not a hidden aircraft_id.
import type { QueryResults } from "./DataGridPremium";

/**
 * What the column shows for `field`. Its TYPE is the column's type to the sort:
 * a string always sorts as numeric-aware text (an outline "2.2 …" before
 * "2.10 …"), a number as a number. So a numeric column whose API value is a
 * decimal STRING ("-10.00", "1.5") must return `Number(value)` here, or it
 * sorts as text: negatives and mixed decimal places land out of order.
 */
export type ValueOf<T> = (row: T, field: string) => unknown;

/**
 * A ValueOf that reads the row's own fields and hands the listed NUMERIC
 * fields back as numbers — for an API that serialises decimals as strings
 * ("-10.00"). Blank stays blank; a value that is not a number stays as it is.
 */
export function rowValueOf<T>(numericFields: readonly string[] = []): ValueOf<T> {
  const numeric = new Set(numericFields);
  return (row, field) => {
    const v = (row as unknown as Record<string, unknown>)[field];
    if (!numeric.has(field) || isBlank(v) || typeof v === "number") return v;
    const n = Number(v);
    return Number.isFinite(n) ? n : v;
  };
}

interface ParsedFilter {
  field: string;
  op: string;
  value: unknown;
}

const isBlank = (v: unknown) => v === null || v === undefined || v === "";

const asText = (v: unknown): string => {
  if (isBlank(v)) return "";
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? "" : v.toISOString().slice(0, 10);
  return String(v);
};

/** A comparable number for dates and numbers; null when not comparable. */
const asNumber = (v: unknown): number | null => {
  if (isBlank(v)) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v.getTime();
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const t = new Date(`${s.slice(0, 10)}T00:00:00`).getTime();
    return Number.isNaN(t) ? null : t;
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

/** A date filter compares DAYS: the cell's local day against the value's day.
 *  The filter value reaches `data` JSON-stringified, so a Date the operator
 *  picked (local midnight) arrives as a UTC ISO timestamp — in NZ that is the
 *  PREVIOUS calendar day. Parse the timestamp back and take its LOCAL day. */
const dayOf = (v: unknown): number | null => {
  if (isBlank(v)) return null;
  const d =
    v instanceof Date
      ? v
      : /^\d{4}-\d{2}-\d{2}$/.test(String(v))
      ? new Date(`${String(v)}T00:00:00`)
      : new Date(String(v));
  return Number.isNaN(d.getTime())
    ? null
    : new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

/** `ilike` pattern ("%x%", "x%", "%x") → case-insensitive test. */
function ilike(cell: string, pattern: string): boolean {
  const c = cell.toLowerCase();
  const p = pattern.toLowerCase();
  const starts = p.startsWith("%");
  const ends = p.endsWith("%");
  const core = p.replace(/^%/, "").replace(/%$/, "");
  if (starts && ends) return c.includes(core);
  if (ends) return c.startsWith(core);
  if (starts) return c.endsWith(core);
  return c === core;
}

function matches(cell: unknown, f: ParsedFilter): boolean {
  const { op, value } = f;
  switch (op) {
    case "isEmpty":
      return isBlank(cell) || asText(cell).trim() === "";
    case "isNotEmpty":
      return !(isBlank(cell) || asText(cell).trim() === "");
    case "ilike":
      return ilike(asText(cell), String(value ?? ""));
    case "isAnyOf":
      return Array.isArray(value)
        ? value.some((v) => asText(cell).toLowerCase() === asText(v).toLowerCase())
        : true;
    case "equals":
      return asText(cell).toLowerCase() === asText(value).toLowerCase();
    case "=":
    case "!=": {
      const a = asNumber(cell);
      const b = asNumber(value);
      const eq = a !== null && b !== null ? a === b : asText(cell) === asText(value);
      return op === "=" ? eq : !eq;
    }
    case ">":
    case ">=":
    case "<":
    case "<=": {
      const a = asNumber(cell);
      const b = asNumber(value);
      if (a === null || b === null) return false;
      return op === ">" ? a > b : op === ">=" ? a >= b : op === "<" ? a < b : a <= b;
    }
    case "is":
    case "not":
    case "after":
    case "onOrAfter":
    case "before":
    case "onOrBefore": {
      const a = dayOf(cell);
      const b = dayOf(value);
      if (b === null) return true; // an unfinished date filter narrows nothing
      if (a === null) return op === "not";
      if (op === "is") return a === b;
      if (op === "not") return a !== b;
      if (op === "after") return a > b;
      if (op === "onOrAfter") return a >= b;
      if (op === "before") return a < b;
      return a <= b;
    }
    default:
      // An operator this helper does not know narrows nothing rather than
      // emptying the grid for a reason no one can see.
      return true;
  }
}

function parseFilters(raw: unknown): ParsedFilter[] {
  const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
  const out: ParsedFilter[] = [];
  for (const item of list) {
    try {
      const f = typeof item === "string" ? JSON.parse(item) : item;
      if (f && typeof f.field === "string") {
        out.push({ field: f.field, op: String(f.op ?? "ilike"), value: f.value });
      }
    } catch {
      // not a filter this grid wrote — ignore it
    }
  }
  return out;
}

/** Apply the grid's `sort` param ("-date,id"). Blanks sort last either way. */
export function sortRows<T>(rows: T[], sort: unknown, valueOf: ValueOf<T>): T[] {
  const keys = String(sort ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => ({ desc: s.startsWith("-"), field: s.replace(/^-/, "") }));
  if (keys.length === 0) return rows;
  return [...rows].sort((a, b) => {
    for (const k of keys) {
      const va = valueOf(a, k.field);
      const vb = valueOf(b, k.field);
      const aBlank = isBlank(va);
      const bBlank = isBlank(vb);
      if (aBlank || bBlank) {
        if (aBlank && bBlank) continue;
        return aBlank ? 1 : -1;
      }
      const na = asNumber(va);
      const nb = asNumber(vb);
      const c =
        typeof va !== "string" && na !== null && nb !== null
          ? na - nb
          : String(va).localeCompare(String(vb), undefined, { numeric: true });
      if (c !== 0) return k.desc ? -c : c;
    }
    return 0;
  });
}

/**
 * Find (`q`: any listed field contains the text), FILTERS (every item must
 * match), sort, then page. `total` is the count AFTER Find/FILTERS — the pager
 * counts what the operator can page through.
 */
export function applyClientGridParams<T>(
  rows: T[],
  params: { [key: string]: any },
  valueOf: ValueOf<T>,
  findFields: string[]
): QueryResults<T> {
  const q = String(params.q ?? "").trim().toLowerCase();
  const filters = parseFilters(params.filter);
  let out = rows;
  if (q) {
    out = out.filter((r) => findFields.some((f) => asText(valueOf(r, f)).toLowerCase().includes(q)));
  }
  if (filters.length > 0) {
    out = out.filter((r) => filters.every((f) => matches(valueOf(r, f.field), f)));
  }
  out = sortRows(out, params.sort, valueOf);
  const page = Number(params.page ?? 1);
  const size = Number(params.size ?? 15);
  return { total: out.length, rows: out.slice((page - 1) * size, page * size) };
}
